/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { getProperties, action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import SifAuthenticationMethod from 'linced-admin/enums/sif-authentication-method';
import SifCountry from 'linced-admin/enums/sif-country';
import SifEncoding from 'linced-admin/enums/sif-encoding';
import SifMatrixParamLocation from 'linced-admin/enums/sif-matrix-param-location';

export default class SifZone extends Component {
  @action
  setApplicationKey(value) {
    this.applicationKey = value;
  }

  @action
  setBaseUrl(value) {
    this.baseUrl = value;
  }

  @action
  setName(value) {
    this.name = value;
  }

  @action
  setOauthScopes(value) {
    this.oauthScopes = value;
  }

  @action
  setOauthTokenUrl(value) {
    this.oauthTokenUrl = value;
  }

  @action
  setPassword(value) {
    this.password = value;
  }

  @action
  setSessionToken(value) {
    this.sessionToken = value;
  }

  @action
  setZoneIdPattern(value) {
    this.zoneIdPattern = value;
  }

  // Attrs
  zoneProperties = Object.freeze([
    'id',
    'name',
    'jurisdiction',
    'baseUrl',
    'applicationKey',
    'password',
    'sessionToken',
    'authenticationMethod',
    'zoneIdPattern',
    'oauthScopes',
    'oauthTokenUrl',
    'encoding',
    'matrixParamLocation',
  ]);

  Jurisdiction = Jurisdiction;
  SifAuthenticationMethod = SifAuthenticationMethod;
  SifCountry = SifCountry;
  SifEncoding = SifEncoding;
  SifMatrixParamLocation = SifMatrixParamLocation;

  // Services
  @service
  session;

  @service
  store;

  @service
  toaster;

  // Props
  // country provides the country to work within.
  @tracked country = SifCountry.AUSTRALIA;

  // entityName provides the full name of the zone entity for this country.
  get entityName() {
    return `sif/${this.country}/zone`;
  }

  // zone provides the SIF Zone to be editing.
  zone = null;

  // newZone provides a boolean to enable creating a brand new SIF Zone.
  newZone = false;

  // onCreate provides the callback function to return the newly created
  // Zone to show the returned client ID and Secret.
  //
  // @param {DS.Model<module:linced-admin/models/sif/zone} - the SIF zone.
  onCreate() {}

  // State
  // zoneEditOpen provides the state of the Zone edit dialog.
  zoneEditOpen = false;

  // zoneDeleteOpen provides the state of the Zone delete dialog.
  @tracked
  zoneDeleteOpen = false;

  // saveInProgress provides the current state of when a record save is in
  // progress.
  @tracked
  saveInProgress = false;

  /* Zone properties State */
  // id contains the provided zone's id field, to enable operations to happen
  // on the copied attribute, rather than the datastore object itself in order
  // to prevent data munging.
  @tracked
  id = '';

  // name contains the provided zone's name field, to enable operations to
  // happen on the copied attribute, rather than the datastore object itself in
  // order to prevent data munging.
  @tracked
  name = '';

  // jurisdiction contains the provided zone's jurisdiction field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  jurisdiction = null;

  // baseUrl contains the provided zone's baseUrl field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  baseUrl = '';

  // applicationKey contains the provided zone's applicationKey field, to
  // enable operations to happen on the copied attribute, rather than the
  // datastore object itself in order to prevent data munging.
  @tracked
  applicationKey = '';

  // password contains the provided client's password field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  password = '';

  // sessionToken contains the provided client's sessionToken field,
  // to enable operations to happen on the copied attribute, rather than the
  // datastore object itself in order to prevent data munging.
  @tracked
  sessionToken = '';

  // authenticationMethod contains the provided client's authenticationMethod
  // field, to enable operations to happen on the copied attribute, rather than
  // the datastore object itself in order to prevent data munging.
  @tracked
  authenticationMethod = SifAuthenticationMethod.BASIC;

  // zoneIdPattern contains a pattern for appending a Zone ID to requests.
  @tracked
  zoneIdPattern = '';

  // oauthScopes contains a comma-separated list of scopes to request when
  // using oauth2 client credentials authentication.
  @tracked
  oauthScopes = '';

  // oauthTokenUrl contains the oauth2 token endpoint URL, for use with
  // oauth2 client credentials authentication.
  @tracked
  oauthTokenUrl = '';

  // encoding denotes the encoding method which this Zone uses.
  @tracked
  encoding = SifEncoding.XML;

  // matrixParamLocation specifies where matrix parameters should be added to
  // HTTP requests.
  @tracked
  matrixParamLocation = SifMatrixParamLocation.URL;

  @tracked
  deleteInProgress;

  @tracked
  uploading;

  // Methods
  /**
   * resetForm resets the required state properties to defaults.
   */
  resetForm() {
    this.id = '';
    this.name = '';
    this.jurisdiction = null;
    this.baseUrl = '';
    this.applicationKey = '';
    this.password = '';
    this.sessionToken = '';
    this.authenticationMethod = SifAuthenticationMethod.BASIC;
    this.zoneIdPattern = '';
    this.oauthScopes = '';
    this.oauthTokenUrl = '';
    this.encoding = SifEncoding.XML;
    this.matrixParamLocation = SifMatrixParamLocation.URL;
    // State reset
    this.uploading = false;
  }

  /**
   * toggleEdit toggles the edit zone dialog open, resets the form and
   * creates a deep copy of the provided zone into the component's zone
   * state variables, ready for editing.
   */
  toggleEdit() {
    let zone = this.zone;

    this.toggleProperty('zoneEditOpen');
    if (!this.zoneEditOpen) {
      this.resetForm();
      return;
    }

    if (!isEmpty(zone)) {
      this.setProperties(zone.getProperties(this.zoneProperties));
    }
  }

  /**
   * createZone creates a new SIF Zone on the server.
   *
   * @param {Object} props - contains the zone properties that are wanting to
   *     be pushed into the zone on create.
   */
  createZone(props) {
    let newClient = this.store.createRecord(
      this.entityName,
      getProperties(this, props),
    );

    newClient
      .save()
      .then((zone) => {
        this.onCreate(zone);
        this.toaster.success('SIF Zone created successfully!');
        this.toggleEdit();
      })
      .catch((err) => {
        this.toaster.error(`Error creating new SIF Zone! ${err}`);
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  /**
   * updateZone updates the current SIF Zone on the server.
   *
   * @param {Object} props - contains the zone properties that are wanting to
   *     be pushed into the zone on update.
   */
  updateZone(props) {
    this.store
      .findRecord(this.entityName, this.id)
      .then((zone) => {
        zone.setProperties(getProperties(this, props));

        return zone.save();
      })
      .then(() => {
        this.toaster.success(`SIF Zone saved successfully!`);
        this.toggleEdit();
      })
      .catch((err) => {
        this.toaster.error(`Error saving SIF Zone! ${err}`);
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  /**
   * deleteZone deletes the current SIF Zone from the server.
   */
  deleteZone() {
    this.deleteInProgress = true;
    if (!isEmpty(this.onDelete)) {
      this.onDelete(this.zone);

      this.deleteInProgress = false;
      this.zoneDeleteOpen = false;
      return;
    }

    this.store
      .findRecord(this.entityName, this.zone.id, { backgroundReload: false })
      .then(function (zone) {
        zone.destroyRecord();
      })
      .then(() => {
        this.toaster.success(`SIF Zone deleted successfully!`);
      })
      .catch(() => {
        this.toaster.error(`Error deleting SIF Zone!`);
      })
      .finally(() => {
        // A successful destroyRecord() removes the zone from the store,
        // which can tear down this component (e.g. via a list re-render)
        // before this callback fires -- guard against setting properties
        // on an already-destroyed component.
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        this.deleteInProgress = false;
        this.zoneDeleteOpen = false;
      });
  }

  @action
  noOp() {}

  @action
  changeJurisdiction(jurisdiction) {
    this.jurisdiction = jurisdiction.value;
  }

  @action
  changeAuthMethod(method) {
    this.authenticationMethod = method.value;
  }

  @action
  changeEncoding(encoding) {
    this.encoding = encoding.value;
  }

  @action
  changeMatrixParamLocation(location) {
    this.matrixParamLocation = location.value;
  }

  // onSave performs either a save, or update based on whether the provided
  // newZone property is set to true or false.
  @action
  onSave() {
    let properties = this.zoneProperties;
    this.saveInProgress = true;

    if (this.newZone) {
      this.createZone(properties);
    } else {
      this.updateZone(properties);
    }
  }

  // onDelete performs a zone deletion.
  @action
  onDelete() {
    this.deleteZone();
  }

  // toggleZoneEdit toggles the zone edit dialog on or off.
  @action
  toggleZoneEdit() {
    this.toggleEdit();
  }

  // toggleZoneDelete toggles the zone delete dialog on or off.
  @action
  toggleZoneDelete() {
    this.toggleProperty('zoneDeleteOpen');
    if (!this.zoneDeleteOpen) {
      this.resetForm();
    }
  }

  // Life Cycle hooks
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (isEmpty(this.zone)) {
      // If no zone is presented, set everything to expected default values.
      this.resetForm();
    }
  }
}
